<template>
  <main class="pixso-page smart-driving-solution" :style="pageStyle">
    <section class="smart-driving-solution__hero" aria-labelledby="smart-driving-hero-title">
      <div class="smart-driving-solution__hero-inner">
        <h1 id="smart-driving-hero-title" class="smart-driving-solution__hero-title">
          {{ t('SolutionView7.hero.title') }}
        </h1>
      </div>
    </section>

    <section class="smart-driving-solution__intro" aria-labelledby="smart-driving-intro-title">
      <div class="smart-driving-solution__copy smart-driving-solution__copy--intro">
        <p class="smart-driving-solution__eyebrow">{{ t('SolutionView7.intro.eyebrow') }}</p>
        <h2 id="smart-driving-intro-title" class="smart-driving-solution__headline">
          {{ t('SolutionView7.intro.title') }}
        </h2>
      </div>

      <figure class="smart-driving-solution__media smart-driving-solution__media--video">
        <button
          v-if="!introVideoPlaying"
          type="button"
          class="smart-driving-solution__video-cover"
          :aria-label="t('SolutionView7.alt.introVideo')"
          @click="startIntroVideo"
        >
          <img :src="introVideoCover" :alt="t('SolutionView7.alt.introVideo')" />
        </button>
        <video
          :key="introVideoKey"
          ref="introVideoRef"
          v-show="introVideoPlaying"
          :src="currentIntroVideo"
          :aria-label="t('SolutionView7.alt.introVideo')"
          controls
          playsinline
          preload="metadata"
        />
      </figure>
    </section>

    <section
      class="smart-driving-solution__panel smart-driving-solution__panel--commercial"
      aria-labelledby="smart-driving-commercial-title"
    >
      <div class="smart-driving-solution__panel-inner">
        <div class="smart-driving-solution__copy smart-driving-solution__copy--scenario smart-driving-solution__copy--commercial">
          <p class="smart-driving-solution__brand-mark" data-text-reveal="off">CXROBOT</p>
          <h2 id="smart-driving-commercial-title" class="smart-driving-solution__scenario-title">
            {{ t('SolutionView7.commercial.title') }}
          </h2>
          <p class="smart-driving-solution__scenario-subtitle">{{ t('SolutionView7.commercial.subtitle') }}</p>
        </div>

        <figure class="smart-driving-solution__media smart-driving-solution__media--commercial">
          <img
            :src="commercialDiagram"
            :alt="t('SolutionView7.alt.commercial')"
            loading="lazy"
          />
        </figure>
      </div>
    </section>

    <section
      class="smart-driving-solution__panel smart-driving-solution__panel--passenger"
      aria-labelledby="smart-driving-passenger-title"
    >
      <div class="smart-driving-solution__panel-inner">
        <div class="smart-driving-solution__copy smart-driving-solution__copy--scenario smart-driving-solution__copy--commercial">
          <p class="smart-driving-solution__brand-mark" data-text-reveal="off">CXROBOT</p>
          <h2 id="smart-driving-passenger-title" class="smart-driving-solution__scenario-title">
            {{ t('SolutionView7.passenger.title') }}
          </h2>
          <p class="smart-driving-solution__scenario-subtitle">{{ t('SolutionView7.passenger.subtitle') }}</p>
        </div>

        <figure class="smart-driving-solution__media smart-driving-solution__media--passenger-top">
          <img
            :src="passengerFlowImage"
            :alt="t('SolutionView7.alt.passengerFlow')"
            loading="lazy"
          />
        </figure>

        <figure class="smart-driving-solution__media smart-driving-solution__media--passenger-bottom">
          <img
            :src="passengerCarImage"
            :alt="t('SolutionView7.alt.passengerCar')"
            loading="lazy"
          />
        </figure>
      </div>
    </section>

    <section
      class="smart-driving-solution__panel smart-driving-solution__panel--fusion"
      aria-labelledby="smart-driving-fusion-title"
    >
      <div class="smart-driving-solution__panel-inner smart-driving-solution__panel-inner--fusion">
        <div class="smart-driving-solution__copy smart-driving-solution__copy--fusion-product">
          <h2
            class="smart-driving-solution__scenario-title smart-driving-solution__scenario-title--product"
            :style="fusionProductTitleStyle"
          >
            {{ t('SolutionView7.fusion.productTitle') }}
          </h2>
        </div>

        <figure class="smart-driving-solution__media smart-driving-solution__media--fusion-main">
          <img
            :src="fusionMainImage"
            :alt="t('SolutionView7.alt.fusionMain')"
            loading="lazy"
          />
        </figure>

        <div class="smart-driving-solution__copy smart-driving-solution__copy--fusion-scenario">
          <p class="smart-driving-solution__brand-mark" data-text-reveal="off">CXROBOT</p>
          <h2 id="smart-driving-fusion-title" class="smart-driving-solution__scenario-title">
            {{ t('SolutionView7.fusion.title') }}
          </h2>
          <p
            v-if="t('SolutionView7.fusion.subtitle')"
            class="smart-driving-solution__scenario-subtitle smart-driving-solution__scenario-subtitle--fusion"
          >
            {{ t('SolutionView7.fusion.subtitle') }}
          </p>
        </div>

        <figure class="smart-driving-solution__media smart-driving-solution__media--fusion-bottom">
          <img
            :src="fusionBottomImage"
            :alt="t('SolutionView7.alt.fusionBottom')"
            loading="lazy"
          />
        </figure>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import heroBackgroundZh from '@/assets/images/pixso-smart-driving-solution/bannerproductinertia.png'
import heroBackgroundEn from '@/assets/i18n/en/home/scenario-smart-driving.jpg'
import introVideoCover from '@/assets/images/pixso-smart-driving-solution/Frame_314_191.png'
import introVideo from '@/assets/images/pixso-smart-driving-solution/车规.mp4'
import introVideoEn from '@/assets/images/pixso-smart-driving-solution/smartCarEn.mp4'
import panelBackground from '@/assets/images/pixso-smart-driving-solution/2b69a9c6ddef5fa41e4130ea33c66e2caefb7e30.png'
import commercialDiagramZh from '@/assets/images/pixso-smart-driving-solution/1ea6187bb6bdc9c8e47cabd9e921194c8a52ae3d.png'
import commercialDiagramEn from '@/assets/i18n/en/solution/smart-driving/commercial-vehicle.png'
import passengerFlowImageZh from '@/assets/images/pixso-smart-driving-solution/5c976ef366ea72c82c7a6b05fa66953b701c6923.png'
import passengerFlowImageEn from '@/assets/i18n/en/solution/smart-driving/passenger-ins-combo.png'
import passengerCarImageZh from '@/assets/images/pixso-smart-driving-solution/8f4b4530dfd26178bd4c6da6da856ace8974469f.png'
import passengerCarImageEn from '@/assets/i18n/en/solution/smart-driving/passenger-three-piece.png'
import fusionMainImageZh from '@/assets/images/pixso-smart-driving-solution/e88d139bef5c7747ccfe6ce80c133a06d791989e.png'
import fusionMainImageEn from '@/assets/i18n/en/solution/smart-driving/fusion-cpfusion.png'
import fusionBottomImageZh from '@/assets/images/pixso-smart-driving-solution/983137018e35cc50a49b320ea9da610f79547f89.png'
import fusionBottomImageEn from '@/assets/i18n/en/solution/smart-driving/fusion-vio.png'

const { t, locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')
const heroBackground = computed(() => (isEnglish.value ? heroBackgroundEn : heroBackgroundZh))
const currentIntroVideo = computed(() => (isEnglish.value ? introVideoEn : introVideo))
const introVideoKey = computed(() => `intro-video-${locale.value}`)
const commercialDiagram = computed(() => (isEnglish.value ? commercialDiagramEn : commercialDiagramZh))
const passengerFlowImage = computed(() => (isEnglish.value ? passengerFlowImageEn : passengerFlowImageZh))
const passengerCarImage = computed(() => (isEnglish.value ? passengerCarImageEn : passengerCarImageZh))
const fusionMainImage = computed(() => (isEnglish.value ? fusionMainImageEn : fusionMainImageZh))
const fusionBottomImage = computed(() => (isEnglish.value ? fusionBottomImageEn : fusionBottomImageZh))

const pageStyle = computed(() => ({
  '--smart-driving-hero-bg': `url(${heroBackground.value})`,
  '--smart-driving-panel-bg': `url(${panelBackground})`
}))

const fusionProductTitleStyle = {
  color: 'var(--unnamed, rgba(0, 0, 0, 1))',
  fontFamily: 'var(--pixso-font-cn)',
  fontSize: 'var(--pixso-fs-56)',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'var(--pixso-lh-56)',
  letterSpacing: '0',
  textAlign: 'center'
}

const introVideoPlaying = ref(false)
const introVideoRef = ref(null)

const startIntroVideo = () => {
  introVideoPlaying.value = true
  introVideoRef.value?.play()
}
</script>

<style scoped>
.smart-driving-solution {
  width: 100%;
  overflow-x: hidden;
  background-color: #d7e9f3;
  color: #111111;
  font-family: var(--pixso-font-cn);
}

.smart-driving-solution__hero {
  width: 100%;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  overflow: hidden;
  background-image: var(--smart-driving-hero-bg);
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
}

.smart-driving-solution__hero-inner {
  width: min(calc(100% - var(--pixso-page-padding) * 2), 1152px);
  height: 100%;
  margin: 0 auto;
  padding-top: clamp(96px, 6.25vw, 120px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.smart-driving-solution__hero-title {
  margin: 0;
  color: #ffffff;
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  text-align: center;
  text-shadow: 0 4px 18px rgba(0, 0, 0, 0.2);
}

.smart-driving-solution__intro {
  width: 100%;
  min-height: 1027px;
  padding: 96px 0 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
}

.smart-driving-solution__panel {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #d7e9f3;
}

.smart-driving-solution__panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: var(--smart-driving-panel-bg);
  background-position: center top;
  background-size: cover;
  background-repeat: no-repeat;
  pointer-events: none;
}

.smart-driving-solution__panel--passenger::before {
  transform: scaleY(-1);
  transform-origin: center;
}

.smart-driving-solution__panel-inner {
  position: relative;
  z-index: 1;
  width: min(calc(100% - var(--pixso-page-padding) * 2), 1152px);
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.smart-driving-solution__panel--commercial {
  min-height: 1800px;
}

.smart-driving-solution__panel--commercial .smart-driving-solution__panel-inner {
  padding-top: 180px;
  padding-bottom: 180px;
}

.smart-driving-solution__panel--passenger {
  min-height: 1839px;
}

.smart-driving-solution__panel--passenger .smart-driving-solution__panel-inner {
  padding-top: 163px;
  padding-bottom: 104px;
}

.smart-driving-solution__panel--fusion {
  min-height: 3611px;
}

.smart-driving-solution__panel--fusion .smart-driving-solution__panel-inner {
  width: min(calc(100% - var(--pixso-page-padding) * 2), 1256px);
  padding-top: 560px;
  padding-bottom: 180px;
}

.smart-driving-solution__panel-inner--fusion {
  gap: 0;
}

.smart-driving-solution__copy {
  width: min(100%, 1042px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.smart-driving-solution__copy--intro {
  gap: 12px;
}

.smart-driving-solution__copy--scenario {
  gap: 0;
}

.smart-driving-solution__copy--commercial .smart-driving-solution__scenario-subtitle {
  margin-top: var(--pixso-gap-lg);
}

.smart-driving-solution__copy--fusion-product {
  width: min(100%, 1256px);
  gap: 0;
}

.smart-driving-solution__copy--fusion-scenario {
  width: min(100%, 1256px);
  gap: 0;
  margin-top: var(--pixso-section-gap-xl);
}

.smart-driving-solution__copy--scenario .smart-driving-solution__brand-mark,
.smart-driving-solution__copy--fusion-scenario .smart-driving-solution__brand-mark {
  z-index: 0;
}

.smart-driving-solution__copy--scenario .smart-driving-solution__scenario-title {
  z-index: 1;
  margin-top: -44px;
}

.smart-driving-solution__copy--fusion-scenario .smart-driving-solution__scenario-title {
  z-index: 1;
  margin-top: -58px;
}

.smart-driving-solution__copy--fusion-product .smart-driving-solution__scenario-title {
  margin-top: 0;
  color: var(--unnamed, rgba(0, 0, 0, 1));
  font-family: var(--pixso-font-cn);
  font-size: var(--pixso-fs-56);
  font-style: normal;
  font-weight: 700;
  line-height: var(--pixso-lh-56);
  letter-spacing: 0;
  text-align: center;
}

.smart-driving-solution__eyebrow {
  margin: 0;
  color: var(--pixso-color-primary);
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-28);
  font-style: italic;
  font-weight: 400;
  line-height: var(--pixso-lh-28);
}

.smart-driving-solution__headline {
  margin: 0;
  color: #000000;
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.smart-driving-solution__brand-mark {
  position: relative;
  margin: 0;
  color: rgba(172, 191, 84, 0.8);
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.smart-driving-solution__scenario-title {
  position: relative;
  margin: -22px 0 0;
  color: #000000;
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.smart-driving-solution__scenario-subtitle {
  margin: 0;
  color: #000000;
  font-size: var(--pixso-fs-56);
  font-weight: 700;
  line-height: var(--pixso-lh-56);
}

.smart-driving-solution__scenario-subtitle--fusion {
  margin-top: 4px;
}

.smart-driving-solution__media {
  position: relative;
  margin: 0;
  overflow: hidden;
}

.smart-driving-solution__video-cover {
  position: absolute;
  inset: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  z-index: 1;
}

.smart-driving-solution__video-cover img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

.smart-driving-solution__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.smart-driving-solution__media video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

.smart-driving-solution__media--commercial img,
.smart-driving-solution__media--passenger-top img,
.smart-driving-solution__media--passenger-bottom img,
.smart-driving-solution__media--fusion-bottom img {
  object-fit: contain;
}

.smart-driving-solution__media--video {
  width: min(100%, 960px);
  aspect-ratio: 16 / 9;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(27, 50, 77, 0.16);
}

.smart-driving-solution__media--commercial {
  width: min(100%, 1152px);
  height: 1140px;
  margin-top: 0;
}

.smart-driving-solution__media--passenger-top {
  width: min(100%, 1152px);
  height: 720px;
  margin-top: 115px;
}

.smart-driving-solution__media--passenger-bottom {
  width: min(100%, 1152px);
  height: 720px;
}

.smart-driving-solution__media--fusion-main {
  width: min(100%, 1179px);
  height: 717px;
  margin-top: var(--pixso-gap-md);
}

.smart-driving-solution__media--fusion-main img {
  object-fit: contain;
}

.smart-driving-solution__media--fusion-bottom {
  width: min(100%, 1256px);
  height: 1145px;
  margin-top: 0;
}

@media (max-width: 1279px) {
  .smart-driving-solution__intro {
    min-height: auto;
    padding: 72px 0 72px;
    gap: 32px;
  }

  .smart-driving-solution__panel--commercial,
  .smart-driving-solution__panel--passenger,
  .smart-driving-solution__panel--fusion {
    min-height: auto;
  }

  .smart-driving-solution__panel-inner {
    width: min(calc(100% - var(--pixso-page-padding) * 2), 1152px);
    padding-top: 96px;
    padding-bottom: 96px;
  }

  .smart-driving-solution__panel--commercial .smart-driving-solution__panel-inner {
    padding-top: 96px;
    padding-bottom: 96px;
  }

  .smart-driving-solution__panel--passenger .smart-driving-solution__panel-inner {
    padding-top: 96px;
    padding-bottom: 96px;
  }

  .smart-driving-solution__panel--fusion .smart-driving-solution__panel-inner {
    width: min(calc(100% - var(--pixso-page-padding) * 2), 1256px);
    padding-top: 120px;
    padding-bottom: 120px;
  }

  .smart-driving-solution__media--commercial {
    height: auto;
  }

  .smart-driving-solution__media--passenger-top,
  .smart-driving-solution__media--passenger-bottom,
  .smart-driving-solution__media--fusion-main,
  .smart-driving-solution__media--fusion-bottom {
    height: auto;
  }

  .smart-driving-solution__media--fusion-bottom {
    margin-top: 120px;
  }
}

@media (max-width: 767px) {
  .smart-driving-solution__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .smart-driving-solution__hero-inner {
    padding-top: 56px;
  }

  .smart-driving-solution__hero-title {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }

  .smart-driving-solution__intro {
    padding: 56px 0 64px;
    gap: 24px;
  }

  .smart-driving-solution__headline {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }

  .smart-driving-solution__eyebrow {
    font-size: var(--pixso-fs-28);
    line-height: var(--pixso-lh-28);
  }

  .smart-driving-solution__panel-inner {
    padding-top: 64px;
    padding-bottom: 72px;
  }

  .smart-driving-solution__brand-mark {
    font-size: var(--pixso-fs-64);
  }

  .smart-driving-solution__scenario-title {
    margin-top: -12px;
    font-size: var(--pixso-fs-28);
    line-height: var(--pixso-lh-28);
  }

  .smart-driving-solution__copy--scenario .smart-driving-solution__scenario-title {
    margin-top: -20px;
  }

  .smart-driving-solution__copy--fusion-scenario .smart-driving-solution__scenario-title {
    margin-top: -28px;
  }

  .smart-driving-solution__copy--fusion-scenario {
    margin-top: var(--pixso-section-gap-xl);
  }

  .smart-driving-solution__scenario-subtitle {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .smart-driving-solution__media--video {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .smart-driving-solution__media--commercial,
  .smart-driving-solution__media--passenger-top,
  .smart-driving-solution__media--passenger-bottom,
  .smart-driving-solution__media--fusion-main,
  .smart-driving-solution__media--fusion-bottom {
    width: 100%;
    height: auto;
    aspect-ratio: auto;
  }

  .smart-driving-solution__media--commercial {
    margin-top: 32px;
  }

  .smart-driving-solution__media--passenger-top {
    margin-top: 48px;
  }

  .smart-driving-solution__media--fusion-main {
    margin-top: var(--pixso-gap-sm);
  }

  .smart-driving-solution__media--fusion-bottom {
    margin-top: 0;
  }
}
</style>
